import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import back from '@shared/assets/sprites/arrow_back.png';
import noAvatar from '@shared/assets/images/no_avatar.svg';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import { useDispatch } from 'react-redux';
import { User } from '../../../types/interfaces';
import { AppDispatch } from '../../../app/store/store';
import { logout } from '../../../entities/auth/model';
import { clearRememberMeData } from '@entities/auth/lib';

// Заголовки страниц для хедера. Сравниваем по префиксу пути и берём
// первое совпадение, поэтому порядок важен — более специфичные пути
// должны идти выше общих (например, /helpdesk/:id раньше /helpdesk).
const PAGE_TITLES: Array<{ test: (p: string) => boolean; title: string }> = [
  { test: (p) => p === '/', title: 'Главная' },
  { test: (p) => p.startsWith('/catalog'), title: 'Каталог' },
  { test: (p) => p.startsWith('/cart'), title: 'Корзина' },
  { test: (p) => p.startsWith('/prices'), title: 'Прайсы' },
  { test: (p) => p.startsWith('/managers'), title: 'Менеджеры' },
  { test: (p) => p.startsWith('/companies'), title: 'Клиенты' },
  { test: (p) => p.startsWith('/company'), title: 'Компания' },
  { test: (p) => p.startsWith('/orders'), title: 'История заказов' },
  { test: (p) => p.startsWith('/devices'), title: 'Устройства' },
  { test: (p) => p.startsWith('/helpdesk'), title: 'Поддержка' },
  { test: (p) => p.startsWith('/contacts'), title: 'Контакты' },
  { test: (p) => p.startsWith('/settings'), title: 'Настройки' },
  { test: (p) => p.startsWith('/profile'), title: 'Профиль' },
  { test: (p) => p.startsWith('/issues'), title: 'Заявки' },
];

const getPageTitle = (pathname: string): string =>
  PAGE_TITLES.find((entry) => entry.test(pathname))?.title ?? '';

const Header = ({ me }: { me: User }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationsArray = location.pathname.split('/');
  const dispatch = useDispatch<AppDispatch>();
  // На главной по макету вместо «Главная» — приветствие (Bitrix 35179, п.1–2).
  const pageTitle =
    location.pathname === '/'
      ? `Добро пожаловать${me?.first_name ? `, ${me.first_name}` : ''}!`
      : getPageTitle(location.pathname);

  // Кнопку «Назад» имеет смысл показывать только на вложенных страницах,
  // где у пользователя нет своего внутреннего сайдбара/таб-навигации.
  // В /settings/* для возврата используется внутреннее меню — там back
  // лишний и ломает визуал шапки.
  const showBackButton =
    locationsArray.length > 2 &&
    !location.pathname.startsWith('/issues') &&
    !location.pathname.startsWith('/settings');

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const modalRoot = document.getElementById('modal-root');
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !modalRoot?.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    navigate('/profile');
    setIsMenuOpen(false);
  };

  const handleLogoutClick = () => {
    clearRememberMeData();
    dispatch(logout());
    window.location.replace('/login');
  };

  return (
    // -mx-4 -mt-4 компенсируют p-4 контейнера Area, чтобы белый фон
    // шапки уходил вплотную к верхнему/левому/правому краю правой колонки
    // и образовывал сплошную полосу, как в макете.
    <header className="bg-white -mx-4 -mt-4 mb-4 px-6 py-4 border-b border-[#e5e7eb]">
      <div className="flex justify-between items-center gap-4 min-h-[44px]">
        <nav className="flex items-center gap-3 min-w-0">
          {showBackButton && (
            <button
              className="shrink-0 flex items-center font-medium text-gray-500 hover:[filter:invert(37%)_sepia(93%)_saturate(7471%)_hue-rotate(356deg)_brightness(91%)_contrast(135%)]"
              onClick={() => navigate(-1)}
              aria-label="Назад"
            >
              <img src={back} alt="" className="object-contain" />
            </button>
          )}
          {pageTitle && (
            <h1 className="text-[20px] font-semibold text-[#2d2c2c] truncate">
              {pageTitle}
            </h1>
          )}
        </nav>

        <div ref={menuRef} className="relative">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center gap-2 py-[5px] px-[10px] bg-white rounded-[37px] hover:shadow-sm transition-shadow"
          >
            <img
              src={me.photo || noAvatar}
              alt={me.username || ''}
              className="w-[35px] h-[35px] rounded-full object-cover bg-red-200"
              onError={(e) => { (e.target as HTMLImageElement).src = noAvatar; }}
            />
            {me.username && (
              <div className="ml-1 text-left">
                <p className="font-bold text-sm leading-tight">
                  {me.first_name} {me.last_name}
                </p>
                <p className="text-xs text-gray-500">{me?.company?.name}</p>
              </div>
            )}
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-[10px] shadow-lg min-w-[160px] py-1 overflow-hidden">
              <button
                onClick={handleProfileClick}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
              >
                Профиль
              </button>
              <ConfirmModal
                openButton={
                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors text-red-500">
                    Выйти
                  </button>
                }
                confirm={handleLogoutClick}
                texts={{
                  title: 'Выход',
                  text: 'Вы уверены, что хотите выйти?',
                  cancel: 'Отмена',
                  confirm: 'Выйти',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
